import type { Color, EngineScore } from '../../types';
import { scoreFromPlayerPov } from '../engine/uci';

/**
 * Normalisation et formatage des évaluations moteur. Centralise toute la
 * logique "mat vs centipions" pour que le reste du code (coaching,
 * exercices, UI) manipule des nombres simples et cohérents.
 */

/** Valeur numérique "en centipions équivalents", bornée, pour comparaisons et affichage de jauge. */
export function toComparableCp(score: EngineScore): number {
  if (score.type === 'cp') return clamp(score.value, -2000, 2000);
  // Un mat vaut plus que n'importe quel avantage matériel. On code la
  // distance au mat pour qu'un mat plus proche reste "meilleur".
  const distancePenalty = Math.min(Math.abs(score.value), 50) * 10;
  const magnitude = 3000 - distancePenalty;
  return score.value >= 0 ? magnitude : -magnitude;
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

/** Texte court pour affichage ("+1.4", "Mat en 3", "Mat subi en 2"). */
export function formatScore(score: EngineScore, forPlayer: Color): string {
  const pov = scoreFromPlayerPov(score, forPlayer);
  if (pov.type === 'mate') {
    if (pov.value === 0) return 'Échec et mat';
    return pov.value > 0 ? `Mat en ${pov.value}` : `Mat subi en ${Math.abs(pov.value)}`;
  }
  const pawns = pov.value / 100;
  const sign = pawns > 0 ? '+' : '';
  return `${sign}${pawns.toFixed(1)}`;
}

/** Perte en "centipions équivalents" entre deux scores (déjà dans le même référentiel de couleur). */
export function computeLossCp(before: EngineScore, after: EngineScore): number {
  const loss = toComparableCp(before) - toComparableCp(after);
  return Math.max(0, loss); // le bruit de recherche peut donner un delta légèrement négatif
}
